import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../../components/SEOHead';
import { createClient } from '@supabase/supabase-js';
import Navigation from '../../components/Navigation';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

export default function Support() {
  const [heroSection, setHeroSection] = useState<any>(null);
  const [supportCategories, setSupportCategories] = useState<any>({});
  const [faqItems, setFaqItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('customer-support');

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      // Fetch hero section
      const { data: heroData } = await supabase
        .from('hero_sections')
        .select('*')
        .eq('page', 'support')
        .eq('is_active', true)
        .single();

      if (heroData) setHeroSection(heroData);

      // Fetch support categories
      const { data: categoriesData } = await supabase
        .from('support_categories')
        .select('*')
        .eq('is_active', true)
        .order('display_order', { ascending: true });

      if (categoriesData) {
        const organized: any = {};
        categoriesData.forEach((cat: any) => {
          organized[cat.slug] = cat;
        });
        setSupportCategories(organized);
        if (categoriesData.length > 0) {
          setActiveCategory(categoriesData[0].slug);
        }
      }

      // Fetch FAQ items
      const { data: faqData } = await supabase
        .from('faq_items')
        .select('*')
        .eq('is_active', true)
        .order('display_order', { ascending: true });

      if (faqData) setFaqItems(faqData);

      setLoading(false);
    } catch (error) {
      console.error('Error fetching content:', error);
      setLoading(false);
    }
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    "name": "Support Center",
    "description": "Get comprehensive support for our platform suite including customer support, technical assistance, and service level agreements.",
    "url": `${import.meta.env.VITE_SITE_URL}/support`,
    "mainEntity": {
      "@type": "Organization",
      "name": "Solution Platform Support",
      "contactPoint": [
        {
          "@type": "ContactPoint",
          "telephone": "+1-555-123-4567",
          "contactType": "customer service",
          "email": "support@company.com",
          "availableLanguage": "English"
        },
        {
          "@type": "ContactPoint",
          "telephone": "+1-555-123-4568",
          "contactType": "technical support",
          "email": "technical@company.com",
          "availableLanguage": "English"
        }
      ]
    }
  };

  const defaultCategories = {
    'customer-support': {
      title: 'Customer Support',
      icon: 'ri-customer-service-line',
      description: 'Get help with account management, billing, and general platform questions.',
      options: [
        {
          title: '24/7 Live Chat',
          description: 'Instant support through our live chat system',
          availability: 'Available 24/7',
          responseTime: 'Immediate',
          icon: 'ri-chat-3-line'
        }
      ]
    }
  };

  const categoriesToDisplay = Object.keys(supportCategories).length > 0 ? supportCategories : defaultCategories;

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <i className="ri-loader-4-line text-4xl text-blue-600 animate-spin"></i>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="Support Center - Get Help & Technical Assistance | Solution Platform"
        description="Get comprehensive support for our platform suite including customer support, technical assistance, and service level agreements. 24/7 support available."
        keywords="platform support, technical support, customer service, help center, SLA, platform assistance"
        canonical="/support"
        structuredData={structuredData}
      />

      {/* Navigation */}
      <Navigation />

      {/* Back Button */}
      <div className="bg-gray-50 py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-end">
          <Link to="/" className="inline-flex items-center text-blue-600 hover:text-blue-700 font-medium cursor-pointer">
            <i className="ri-arrow-left-line mr-2"></i>
            Back to Home
          </Link>
        </div>
      </div>

      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-600 to-purple-600 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl font-bold text-white mb-6">
            {heroSection?.title || 'Support Center'}
          </h1>
          <p className="text-xl text-blue-100 max-w-3xl mx-auto">
            {heroSection?.subtitle || 'Get the help you need to succeed with our platform suite. Our expert support team is here to assist you every step of the way.'}
          </p>
        </div>
      </section>

      {/* Quick Contact */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white rounded-lg shadow-md p-6 text-center">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-chat-3-line text-xl text-blue-600"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Live Chat</h3>
              <p className="text-gray-600 mb-4">Get instant help from our support team</p>
              <button className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap cursor-pointer">
                Start Chat
              </button>
            </div>
            
            <div className="bg-white rounded-lg shadow-md p-6 text-center">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-phone-line text-xl text-green-600"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Phone Support</h3>
              <p className="text-gray-600 mb-4">Call us for immediate assistance</p>
              <a href="tel:+15551234567" className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 transition-colors whitespace-nowrap cursor-pointer inline-block">
                +1 (555) 123-4567
              </a>
            </div>
            
            <div className="bg-white rounded-lg shadow-md p-6 text-center">
              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-mail-line text-xl text-purple-600"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Email Support</h3>
              <p className="text-gray-600 mb-4">Send us a detailed message</p>
              <a href="mailto:support@company.com" className="bg-purple-600 text-white px-6 py-2 rounded-lg hover:bg-purple-700 transition-colors whitespace-nowrap cursor-pointer inline-block">
                Email Us
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Support Categories */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-4 gap-8">
            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-xl shadow-lg p-6 sticky top-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Support Categories</h3>
                <nav className="space-y-2">
                  {Object.entries(categoriesToDisplay).map(([key, category]: [string, any]) => (
                    <button
                      key={key}
                      onClick={() => setActiveCategory(key)}
                      className={`w-full flex items-center px-3 py-2 rounded-lg text-left transition-colors cursor-pointer ${
                        activeCategory === key
                          ? 'bg-blue-100 text-blue-700'
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      <i className={`${category.icon} mr-3`}></i>
                      <span className="font-medium text-sm">{category.title}</span>
                    </button>
                  ))}
                </nav>
              </div>
            </div>

            {/* Content */}
            <div className="lg:col-span-3">
              {categoriesToDisplay[activeCategory] && (
                <>
                  <div className="mb-8">
                    <h2 className="text-3xl font-bold text-gray-900 mb-4">
                      {categoriesToDisplay[activeCategory].title}
                    </h2>
                    <p className="text-gray-600">
                      {categoriesToDisplay[activeCategory].description}
                    </p>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    {categoriesToDisplay[activeCategory].options && categoriesToDisplay[activeCategory].options.map((option: any, index: number) => (
                      <div key={index} className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow">
                        <div className="flex items-center mb-4">
                          <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mr-4">
                            <i className={`${option.icon} text-xl text-blue-600`}></i>
                          </div>
                          <div>
                            <h3 className="text-xl font-bold text-gray-900">{option.title}</h3>
                          </div>
                        </div>
                        
                        <p className="text-gray-600 mb-4">{option.description}</p>
                        
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-500">Availability:</span>
                            <span className="font-medium text-gray-900">{option.availability}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-500">Response Time:</span>
                            <span className="font-medium text-gray-900">{option.responseTime}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Find quick answers to common questions about our platform suite.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {faqItems.length > 0 ? (
              <>
                <div className="space-y-6">
                  {faqItems.filter((_, idx) => idx % 2 === 0).map((faq) => (
                    <div key={faq.id} className="bg-white rounded-lg shadow-md p-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">{faq.question}</h3>
                      <p className="text-gray-600">{faq.answer}</p>
                    </div>
                  ))}
                </div>
                
                <div className="space-y-6">
                  {faqItems.filter((_, idx) => idx % 2 === 1).map((faq) => (
                    <div key={faq.id} className="bg-white rounded-lg shadow-md p-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">{faq.question}</h3>
                      <p className="text-gray-600">{faq.answer}</p>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <>
                {/* Default FAQ fallback */}
                <div className="space-y-6">
                  <div className="bg-white rounded-lg shadow-md p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">How do I get started with the platform?</h3>
                    <p className="text-gray-600">You can start with our free 14-day trial. Simply contact our sales team to get your account set up and access to all platform features.</p>
                  </div>
                  
                  <div className="bg-white rounded-lg shadow-md p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">What integrations are available?</h3>
                    <p className="text-gray-600">Our platform supports 200+ pre-built integrations including popular CRM, ERP, and database systems. Custom integrations are also available.</p>
                  </div>
                  
                  <div className="bg-white rounded-lg shadow-md p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">Is training provided?</h3>
                    <p className="text-gray-600">Yes, we provide comprehensive training including video tutorials, documentation, live sessions, and dedicated onboarding support.</p>
                  </div>
                </div>
                
                <div className="space-y-6">
                  <div className="bg-white rounded-lg shadow-md p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">What are the system requirements?</h3>
                    <p className="text-gray-600">Our platform is cloud-based and works with any modern web browser. No special hardware or software installation required.</p>
                  </div>
                  
                  <div className="bg-white rounded-lg shadow-md p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">How secure is the platform?</h3>
                    <p className="text-gray-600">We maintain enterprise-grade security with SOC 2 compliance, encryption at rest and in transit, and regular security audits.</p>
                  </div>
                  
                  <div className="bg-white rounded-lg shadow-md p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">Can I migrate from another platform?</h3>
                    <p className="text-gray-600">Yes, our migration specialists can help you transition from other platforms with minimal downtime and data loss.</p>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-20 bg-blue-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">Still Need Help?</h2>
          <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
            Our support team is standing by to help you succeed. Get in touch and we'll respond quickly.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="bg-white text-blue-600 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer">
              Contact Support
            </button>
            <Link to="/guides" className="border-2 border-white text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-white hover:text-blue-600 transition-colors whitespace-nowrap cursor-pointer">
              Browse Resources
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <img 
                src="https://static.readdy.ai/image/feaac7b19917aec559683ef9ad3d4cd6/f01248d0a1f191b5d9d27b9480b2a552.jpeg" 
                alt="Allgo Digital Logo" 
                className="h-12 w-auto mb-4 filter brightness-0 invert"
              />
              <p className="text-gray-400">
                Transforming enterprise software development with integrated low-code, data flow, and BI solutions.
              </p>
            </div>
            
            <div>
              <h4 className="text-lg font-semibold mb-4">Platforms</h4>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/platforms" className="hover:text-white cursor-pointer">Codee Platform</Link></li>
                <li><Link to="/platforms" className="hover:text-white cursor-pointer">Fusion Platform</Link></li>
                <li><Link to="/platforms" className="hover:text-white cursor-pointer">BI Platform</Link></li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-lg font-semibold mb-4">Resources</h4>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/resources" className="hover:text-white cursor-pointer">Resources</Link></li>
                <li><Link to="/features" className="hover:text-white cursor-pointer">Features</Link></li>
                <li><Link to="/partners" className="hover:text-white cursor-pointer">Partner Program</Link></li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-lg font-semibold mb-4">Contact</h4>
              <ul className="space-y-2 text-gray-400">
                <li>support@company.com</li>
                <li>+1 (555) 123-4567</li>
                <li>Enterprise Solutions</li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2024 Solution Platform. All rights reserved. | <a href="https://readdy.ai/?origin=logo" className="hover:text-white cursor-pointer">Website Builder</a></p>
          </div>
        </div>
      </footer>
    </div>
  );
}
